import React, { memo } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Button } from './components/Button';
import { Card, CardContent } from './components/Card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './components/Accordion';
import { Badge } from './components/Badge';
import { Check, Star, Gift, ArrowRight, Sparkles, BookOpen, Clock, Target, Users, ShieldCheck, Download, Infinity as InfinityIcon, Instagram, Mail, Smartphone, Lock, CreditCard, X, ArrowDown } from 'lucide-react';
import { TestimonialsMarquee } from './components/TestimonialsMarquee';
import { SocialProofNotifications } from './components/SocialProofNotifications';
import { Countdown } from './components/Countdown';
import { TomosSlider } from './components/TomosSlider';
import { BonusSlider } from './components/BonusSlider';
export function App() {
  const shouldReduceMotion = useReducedMotion();
  return <div className="min-h-screen w-full bg-background text-foreground selection:bg-primary/30 selection:text-primary-foreground antialiased" data-id="element-0" data-mp="23ti5fwt74ggi">
      {/* Social proof notifications (fixed bottom-left, non-intrusive) */}
      <SocialProofNotifications data-id="element-1" data-mp="3rcj2nr9n9f1c" />

      {/* 1. Sticky top nav */}
      <nav className="sticky top-0 z-50 w-full border-b border-border/40 bg-background/80 backdrop-blur-md" data-id="element-2" data-mp="3recctgjguhe3">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between" data-id="element-3" data-mp="7sxbo2oq39g">
          <div className="font-heading font-bold text-base md:text-xl tracking-widest text-primary whitespace-nowrap" data-id="element-4" data-mp="2zn66ujh2dpgw">
            Sistema PRO COSMOS
          </div>
          <div className="hidden md:flex items-center space-x-8 text-sm font-medium text-muted-foreground" data-id="element-5" data-mp="38l05i12awv78">
            <a href="#tomos" className="hover:text-primary transition-colors" data-id="element-6" data-mp="3dmfo7mh0apmg">
              Tomos
            </a>
            <a href="#bonus" className="hover:text-primary transition-colors" data-id="element-7" data-mp="nxn69ujvv7of">
              Bonus
            </a>
            <a href="#faq" className="hover:text-primary transition-colors" data-id="element-8" data-mp="swgm3tmn7arr">
              FAQ
            </a>
          </div>
          <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold shadow-[0_0_15px_rgba(212,168,87,0.3)]" data-id="element-9" data-mp="3uday0bte0t6a">
            <a href="https://checkout.librofy.online/cart/53275043332459:1" target="_blank" rel="noopener noreferrer" data-id="element-10" data-mp="85l0anyxes7m">
              Acceder Ahora
            </a>
          </Button>
        </div>
      </nav>

      {/* 2. Hero */}
      <section className="relative pt-3 pb-3 md:pt-8 md:pb-8 overflow-hidden" data-id="element-11" data-mp="3bkgqg2y3g3c3">
        <div className="absolute inset-0 pointer-events-none" data-id="element-12" data-mp="3qcim1at4sa6b">
          <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[900px] h-[900px] bg-primary/[0.06] rounded-full blur-3xl" data-id="element-13" data-mp="3lk7q0j1ih8j1"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10 max-w-4xl text-center" data-id="element-14" data-mp="2xssj0cysi31g">
          <Badge variant="outline" className="mb-3 border-primary/50 text-primary uppercase tracking-widest px-4 py-1.5 text-xs font-semibold bg-primary/5 backdrop-blur-sm" data-id="element-15" data-mp="2svjyi5q65mx1">
            EL SISTEMA QUE ORGANIZA TU ASTROLOGÍA
          </Badge>

          <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold leading-[1.1] mb-5 text-foreground" data-id="element-16" data-mp="ygwjp9pkj8xy">
            Dejá de mirar cartas natales{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-amber-200" data-id="element-17" data-mp="1r3r466mdy8bf">
              sin saber por dónde empezar.
            </span>
          </h1>

          <p className="text-lg md:text-xl text-foreground/85 mb-7 leading-relaxed max-w-2xl mx-auto" data-id="element-18" data-mp="1i3kvcy1mtwil">
            Sistema completo de informes astrológicos listos para entregar. Todo
            tu flujo de trabajo resuelto en un solo{' '}
            <span className="text-primary font-semibold" data-id="element-19" data-mp="6nclebjvfiww">clic</span>.
          </p>

          {/* Static product mockup — visually aligned under subtitle */}
          <div className="relative flex justify-center -mt-[28%] -mb-[6%]" data-id="element-20" data-mp="qx5hw2u8iwcl">
            <motion.img src="https://cdn.magicpatterns.com/uploads/9CPtBwCxrQYkFWLuWqjiCP/COSMOS_COMPLETOS_SIN_FONDOS.png" alt="Sistema PRO COSMOS — colección completa de manuales y bonos de astrología" loading="eager" decoding="async" fetchPriority="high" initial={{
            opacity: 0
          }} animate={{
            opacity: 1,
            scale: shouldReduceMotion ? 1 : [1, 1.025, 1]
          }} transition={{
            opacity: {
              duration: 0.35,
              ease: 'easeOut'
            },
            scale: {
              duration: 6.5,
              ease: 'easeInOut',
              repeat: shouldReduceMotion ? 0 : Infinity
            }
          }} className="relative z-10 mx-auto w-full max-w-none object-contain drop-shadow-[0_14px_16px_rgba(0,0,0,0.22)] pointer-events-none" data-id="element-21" data-mp="qz8fp91mptrv" />
          </div>

          {/* PRIMARY CTA — directly after mockup */}
          <div className="relative mt-4 flex flex-col items-center justify-center gap-3 mb-4" data-id="element-22" data-mp="1w2lt4y7nu9ab">
            <Button asChild size="lg" className="relative w-full sm:w-auto text-base md:text-lg px-8 py-5 h-auto font-extrabold tracking-wide bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_0_50px_rgba(212,168,87,0.6)] ring-2 ring-primary/40 hover:ring-primary/70 transition-all hover:scale-[1.04]" data-id="element-23" data-mp="203xjj5ycyyjk">
              <a href="https://checkout.librofy.online/cart/53275043332459:1" target="_blank" rel="noopener noreferrer" data-id="element-24" data-mp="2jsg4fbcbzpsg">
                QUIERO EL SISTEMA AHORA <ArrowRight className="ml-2 h-5 w-5" data-id="element-25" data-mp="2stwgrompi1zb" />
              </a>
            </Button>
            <a href="#tomos" className="text-sm text-muted-foreground hover:text-primary transition-colors underline-offset-4 hover:underline" data-id="element-26" data-mp="tj4svqxdj91o">
              Ver contenido completo ↓
            </a>
          </div>
        </div>
      </section>

      {/* 2.1 Trust bar + minimalist statistics */}
      <section className="relative py-10 md:py-14 bg-card/20 overflow-hidden" data-id="element-27" data-mp="2as8tuixzg4m0">
        <div className="absolute inset-0 pointer-events-none" data-id="element-28" data-mp="2aq5wfdui3979">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] bg-primary/[0.04] rounded-full blur-3xl" data-id="element-29" data-mp="25s4g30oglnzh"></div>
        </div>

        <div className="container mx-auto px-4 max-w-5xl relative z-10" data-id="element-30" data-mp="2frldyvxgyxkw">
          {/* Premium trust bar — always one row */}
          <motion.ul className="grid grid-cols-3 items-center gap-2 sm:gap-4 pb-5 md:pb-6" initial={{
          opacity: 0,
          y: 12
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-80px'
        }} transition={{
          duration: 0.5,
          ease: 'easeOut'
        }} data-id="element-31" data-mp="2thoxvg8vmf76">
            {[{
            Icon: ShieldCheck,
            label: 'Garantía de 7 días'
          }, {
            Icon: Lock,
            label: 'Pago 100% seguro'
          }, {
            Icon: CreditCard,
            label: 'Mercado Pago | Tarjetas'
          }].map(({
            Icon,
            label
          }, i) => <li key={i} className="flex items-center justify-center gap-1.5 sm:gap-2.5 text-center" data-id="element-32" data-mp="344dixxg14h6i" data-mpi={i}>
                <Icon className="w-4 h-4 sm:w-5 sm:h-5 text-primary shrink-0" strokeWidth={1.5} aria-hidden="true" data-id="element-33" data-mp="2pgsbjrc4fvss" />
                <span className="text-[10px] sm:text-sm md:text-[15px] text-foreground/80 font-medium leading-tight" data-id="element-34" data-mp="2geswh4ko31sh">
                  {label}
                </span>
              </li>)}
          </motion.ul>

          {/* Subtle divider */}
          <div className="h-px w-full bg-gradient-to-r from-transparent via-border/60 to-transparent" aria-hidden="true" data-id="element-35" data-mp="2vvr7m2bflmwu"></div>

          {/* Minimalist statistics — always one row */}
          <div className="grid grid-cols-4 gap-2 sm:gap-4 pt-6 md:pt-8" data-id="element-36" data-mp="2lx32230mv0a1">
            {[{
            num: '+1.000',
            label: 'Astrólogos confían'
          }, {
            num: '500+',
            label: 'Páginas de formación'
          }, {
            num: '4.9',
            label: 'Calificación',
            star: true
          }, {
            num: '6 + 10',
            label: 'Tomos + bonus'
          }].map((stat, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-60px'
          }} transition={{
            duration: 0.5,
            ease: 'easeOut',
            delay: 0.07 * i
          }} className="flex flex-col items-center text-center" data-id="element-37" data-mp="1u76uxxcwrkkj" data-mpi={i}>
                <div className="flex items-baseline justify-center gap-1" data-id="element-38" data-mp="3r4uri6gsay2z">
                  <span className="text-xl sm:text-3xl md:text-4xl lg:text-[2.75rem] font-heading font-black text-primary leading-none tracking-tight whitespace-nowrap" data-id="element-39" data-mp="3r31n6xtn8py5">
                    {stat.num}
                  </span>
                  {stat.star && <Star className="w-3.5 h-3.5 sm:w-5 sm:h-5 text-primary fill-primary shrink-0 translate-y-[-1px]" aria-hidden="true" data-id="element-40" data-mp="33fa7dk8w9us4" />}
                </div>
                <div className="mt-1.5 md:mt-2 text-[8px] sm:text-[10px] md:text-xs text-muted-foreground font-medium uppercase tracking-[0.14em] leading-tight" data-id="element-41" data-mp="2k681tk25qh4p">
                  {stat.label}
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* 2.15 Opiniones — right after trust & statistics */}
      <TestimonialsMarquee data-id="element-42" data-mp="1wi6wek09zgti" />

      {/* 2.2 Emotional pain-points */}
      <section className="relative py-20 md:py-24 overflow-hidden" data-id="element-43" data-mp="21exbp549a8dv">
        <div className="absolute inset-0 pointer-events-none" data-id="element-44" data-mp="26etgh6ks23uh">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/[0.04] rounded-full blur-3xl" data-id="element-45" data-mp="26dihdk2a1k9p"></div>
        </div>

        <div className="container mx-auto px-4 max-w-2xl relative z-10" data-id="element-46" data-mp="1dp33j0uvt7zy">
          {/* Header */}
          <div className="text-center mb-10 md:mb-12" data-id="element-47" data-mp="1io7bjo62kzop">
            <p className="text-[10px] md:text-xs uppercase tracking-[0.35em] text-primary/70 mb-5 font-medium" data-id="element-48" data-mp="1imwavx7x881t">
              Sistema PRO COSMOS
            </p>
            <h2 className="font-heading font-bold text-foreground leading-tight" data-id="element-49" data-mp="3ryf2jdxlk1mn">
              <span className="block text-base md:text-xl tracking-wide uppercase text-foreground/70 mb-2" data-id="element-50" data-mp="3mxre900r5yw4">
                Si después de tantos cursos...
              </span>
              <span className="block text-3xl md:text-5xl uppercase tracking-tight" data-id="element-51" data-mp="3d1682bczyhqp">
                Todavía te pasa{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-amber-200" data-id="element-52" data-mp="388v5f0qvghug">
                  esto...
                </span>
              </span>
            </h2>
            <div className="mx-auto mt-7 h-px w-24 bg-gradient-to-r from-transparent via-primary/60 to-transparent" aria-hidden="true" data-id="element-53" data-mp="2zaauilrjzcgs"></div>
          </div>

          {/* Pain point list */}
          <ul className="rounded-2xl bg-card/30 border border-border/40 backdrop-blur-sm px-5 md:px-8 divide-y divide-primary/10" data-id="element-54" data-mp="2pbtzndphudpg">
            {['Abrís una carta y no sabés qué mirar primero.', 'Saltás entre planetas y aspectos sin entender el panorama completo.', 'Sentís que estudiás mucho pero todo sigue desconectado.', 'Dudás constantemente de tus interpretaciones.', 'Te bloqueás frente a cartas reales.', 'No sabés cómo convertir toda la teoría en una sesión profesional.', 'Sentís que siempre te falta "algo más" para empezar.'].map((pain, i) => <li key={i} className="flex items-center gap-4 py-4 md:py-[18px]" data-id="element-55" data-mp="2pkkrva8cnkca" data-mpi={i}>
                <span className="shrink-0 flex items-center justify-center w-6 h-6 rounded-full border border-primary/40" aria-hidden="true" data-id="element-56" data-mp="2tmpjk3f8m6wa">
                  <X className="w-3 h-3 text-primary/80" strokeWidth={2} data-id="element-57" data-mp="3m41w75pufpzx" />
                </span>
                <p className="text-sm md:text-base text-foreground/85 leading-relaxed" data-id="element-58" data-mp="1ao19owfbcy7d">
                  {pain}
                </p>
              </li>)}
          </ul>

          {/* Transition */}
          <div className="text-center mt-12 md:mt-14" data-id="element-59" data-mp="1kem8ajuzm2qa">
            <p className="font-heading font-bold text-xl md:text-3xl uppercase leading-tight text-foreground" data-id="element-60" data-mp="1kg72279h9hn3">
              ¿Hasta cuándo vas a seguir interpretando{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-amber-200" data-id="element-61" data-mp="rq74kthb36u9">
                sin un sistema
              </span>
              ?
            </p>
            <ArrowDown className="mx-auto mt-6 w-5 h-5 md:w-6 md:h-6 text-primary/70" strokeWidth={1.5} aria-hidden="true" data-id="element-62" data-mp="11omke0avutog" />
          </div>
        </div>
      </section>

      {/* 3. ¿Qué incluye la colección? — Tomos */}
      <section id="tomos" className="py-24 bg-card/30 border-y border-border/50 relative overflow-hidden" data-id="element-63" data-mp="284q9ylom7sen">
        <div className="container mx-auto px-4 max-w-5xl relative z-10" data-id="element-64" data-mp="hoe8j14j4j8g">
          <div className="text-center mb-14" data-id="element-65" data-mp="ml4qxv3t3qx1">
            <h2 className="text-3xl md:text-5xl font-heading font-bold leading-tight text-foreground mb-4" data-id="element-66" data-mp="mo0npg3x49jw">
              ¿Qué incluye la{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-amber-200 italic" data-id="element-67" data-mp="vmurbqfo206y">
                colección?
              </span>
            </h2>
            <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed" data-id="element-68" data-mp="15ksba1qhtatr">
              Cinco tomos estructurados para llevarte desde el caos mental hasta
              interpretaciones claras y profesionales.
            </p>
          </div>

          <TomosSlider data-id="element-69" data-mp="1ef6qk4b0wlcv" />
        </div>
      </section>

      {/* 4. Bonus */}
      <section id="bonus" className="py-24 relative overflow-hidden" data-id="element-70" data-mp="1jdqeubd3a98e">
        <div className="absolute inset-0 pointer-events-none" data-id="element-71" data-mp="1oaguixkzf2ta">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] bg-primary/[0.04] rounded-full blur-3xl" data-id="element-72" data-mp="1o8xaqx04a9mr"></div>
        </div>

        <div className="container mx-auto px-4 max-w-5xl relative z-10" data-id="element-73" data-mp="1tej0e3dauzwf">
          <div className="text-center mb-14" data-id="element-74" data-mp="1oi2b35ud6fhr">
            <div className="flex justify-center mb-5" data-id="element-75" data-mp="1oj3q1lei3m4r">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/50 backdrop-blur-sm shadow-[0_0_20px_rgba(52,211,153,0.15)]" data-id="element-76" data-mp="1kgh1puh1gjcb">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" aria-hidden="true" data-id="element-77" data-mp="2c6buk143ur5q"></span>
                <p className="text-xs md:text-sm font-bold uppercase tracking-wider text-emerald-300" data-id="element-78" data-mp="23etd7mvfa5b2">
                  GRATIS por el día de hoy
                </p>
              </div>
            </div>
            <h2 className="text-3xl md:text-5xl font-heading font-bold leading-tight text-foreground mb-4" data-id="element-79" data-mp="2v4xuvdrsse8l">
              Biblioteca{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-amber-200 italic" data-id="element-80" data-mp="t8fk6g0wg0cd">
                COSMOS™
              </span>
            </h2>
            <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed" data-id="element-81" data-mp="jivw8vybxgan">
              Incluidos sin costo adicional con tu compra de hoy.
            </p>
          </div>

          <BonusSlider data-id="element-82" data-mp="ageb6kueutp8" />
        </div>
      </section>

      {/* 5. ¿Para quién es COSMOS? */}
      <section className="py-24 bg-card/30 border-y border-border/50 relative overflow-hidden" data-id="element-83" data-mp="fgsk053u67ob">
        <div className="absolute inset-0 pointer-events-none" data-id="element-84" data-mp="k1kzwy2qxz8">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/[0.04] rounded-full blur-3xl" data-id="element-85" data-mp="5icto4cmv2fv"></div>
        </div>

        <div className="container mx-auto px-4 max-w-4xl relative z-10" data-id="element-86" data-mp="1uxst2s0r99sk">
          <div className="text-center mb-14" data-id="element-87" data-mp="1zvb0gdz4jgyg">
            <p className="text-xs uppercase tracking-[0.35em] text-primary/70 mb-4 font-medium" data-id="element-88" data-mp="1ztzymjp8oj9z">
              Hecho para vos
            </p>
            <h2 className="text-3xl md:text-5xl font-heading font-bold leading-tight text-foreground" data-id="element-89" data-mp="3fg39wdys2nr3">
              ¿Para quién es{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-amber-200 italic" data-id="element-90" data-mp="3u5iwdhra070z">
                COSMOS?
              </span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-91" data-mp="6xxdrsm5ve05">
            {['Para quienes abren cartas natales y no saben qué mirar primero.', 'Para quienes estudian astrología hace tiempo pero sienten todo desconectado.', 'Para quienes quieren interpretar con claridad y seguridad real.', 'Para quienes desean transformar información suelta en sesiones profesionales.', 'Para quienes sienten que la astrología puede ser más simple cuando existe un sistema.'].map((item, i) => <div key={i} className={'group flex items-start gap-4 p-6 bg-background/60 border border-border/50 rounded-2xl hover:border-primary/40 hover:bg-background/80 transition-all backdrop-blur-sm ' + (i === 4 ? 'md:col-span-2' : '')} data-id="element-92" data-mp="1rxkd3hh5z46p" data-mpi={i}>
                <div className="shrink-0 flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 border border-primary/30 group-hover:bg-primary/15 transition-colors" data-id="element-93" data-mp="26nrlt5hdd8u7">
                  <Check className="w-5 h-5 text-primary" strokeWidth={2.5} data-id="element-94" data-mp="1dxjb1yp5xqpn" />
                </div>
                <p className="text-base md:text-lg text-foreground/90 leading-relaxed" data-id="element-95" data-mp="1iuj9pojonr6q">
                  {item}
                </p>
              </div>)}
          </div>

          <div className="relative pt-12 mt-12 text-center" data-id="element-96" data-mp="2ua7y8kcl4y9n">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-px bg-primary/40" data-id="element-97" data-mp="2za3yq9m2q7kg"></div>
            <p className="font-heading text-xl md:text-2xl italic text-primary leading-relaxed max-w-2xl mx-auto" data-id="element-98" data-mp="nmr2yato104d">
              "COSMOS fue creado para transformar{' '}
              <span className="font-semibold" data-id="element-99" data-mp="iq0lpk64jyhd">caos mental</span> en{' '}
              <span className="font-semibold" data-id="element-100" data-mp="dvwbms1kbac2">interpretaciones claras</span>."
            </p>
          </div>
        </div>
      </section>

      {/* 8. Oferta especial */}
      <section className="py-24 relative overflow-hidden" data-id="element-101" data-mp="3zjcvfr5u6ag">
        <div className="absolute inset-0 pointer-events-none" data-id="element-102" data-mp="3woppggri7dc">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/10 rounded-full blur-3xl" data-id="element-103" data-mp="3w210r6qivj6c"></div>
        </div>

        <div className="container mx-auto px-4 max-w-3xl relative z-10" data-id="element-104" data-mp="3m4wdf3kxxhny">
          <Card className="bg-card/80 backdrop-blur-xl border-primary/30 shadow-[0_0_40px_rgba(212,168,87,0.1)] overflow-hidden" data-id="element-105" data-mp="3m6zced42ml4t">
            <div className="bg-gradient-to-b from-primary/10 to-transparent px-6 pt-12 pb-10 md:px-10 md:pt-14 md:pb-12 text-center border-b border-primary/15" data-id="element-106" data-mp="1kcrpe36kb4ii">
              <h2 className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl leading-tight mb-5 text-foreground" data-id="element-107" data-mp="2c9fah6dtmhje">
                Tu sistema de interpretación{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-amber-200 italic" data-id="element-108" data-mp="22fycrrakciag">
                  empieza hoy.
                </span>
              </h2>
              <p className="text-base md:text-lg text-foreground/80 leading-relaxed max-w-2xl mx-auto mb-8" data-id="element-109" data-mp="1thmer47dai7z">
                Pasá de sentirte perdida frente a una carta natal a interpretar
                con{' '}
                <span className="text-primary/90 font-medium" data-id="element-110" data-mp="1jj6vtoihuf9k">estructura</span>,{' '}
                <span className="text-primary/90 font-medium" data-id="element-111" data-mp="1ekvnji7ukctg">claridad</span> y{' '}
                <span className="text-primary/90 font-medium" data-id="element-112" data-mp="3dwy1kcxpcezq">seguridad</span>.
              </p>

              <div className="flex justify-center mb-4" data-id="element-113" data-mp="38xd64chqqorr">
                <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/8 border border-primary/30 text-primary/95 text-sm font-medium backdrop-blur-sm" role="status" data-id="element-114" data-mp="38vsbkmvff5tw">
                  <span data-id="element-115" data-mp="3q7ywu3ef5v8l">✨</span>
                  <span data-id="element-116" data-mp="3q7pcowg22iat">
                    Quedan{' '}
                    <span className="font-heading font-bold text-primary mx-0.5" data-id="element-117" data-mp="3v6a5wbww41pr">
                      12
                    </span>{' '}
                    accesos disponibles hoy
                  </span>
                </div>
              </div>

              <p className="text-xs md:text-sm text-muted-foreground/85 italic leading-relaxed max-w-xl mx-auto" data-id="element-118" data-mp="32jfoxoza7kh1">
                Cada día más astrólogos están dejando de estudiar información
                suelta y empezando a interpretar con un sistema.
              </p>
            </div>

            <CardContent className="p-8 md:p-14 lg:p-16 bg-gradient-to-b from-card/40 to-card/60" data-id="element-119" data-mp="msbabbdtyics">
              {/* PRICE HERO */}
              <div className="text-center mb-14" data-id="element-120" data-mp="20wb92berzky">
                <p className="text-xs md:text-sm uppercase tracking-[0.3em] text-muted-foreground/80 mb-6 font-medium" data-id="element-121" data-mp="1zl731e1wptb">
                  Todo el sistema completo por menos de lo que cuesta una sola
                  sesión.
                </p>

                <div className="inline-flex items-center gap-2 bg-primary text-primary-foreground text-[10px] md:text-xs font-bold px-3 py-1.5 rounded-full uppercase tracking-[0.2em] mb-8 shadow-[0_0_20px_rgba(212,168,87,0.3)]" data-id="element-122" data-mp="70inb8fi39s6">
                  Hoy acceso completo
                </div>

                <p className="text-sm md:text-base text-muted-foreground mb-3" data-id="element-123" data-mp="3p5vp0zl0or79">
                  Hoy pagás solo
                </p>

                <div className="relative inline-block mb-6" data-id="element-124" data-mp="3kevu1zvgkj7e">
                  <div className="absolute inset-0 -m-8 bg-primary/20 rounded-full blur-3xl pointer-events-none" data-id="element-125" data-mp="2wpkzj04pbl1p"></div>
                  <div className="relative text-7xl md:text-8xl lg:text-9xl font-heading font-black text-primary leading-none tracking-tight drop-shadow-[0_0_30px_rgba(212,168,87,0.5)]" data-id="element-126" data-mp="2rqzwksy2en9k">
                    $19.900
                  </div>
                </div>

                <div className="flex flex-col items-center gap-3" data-id="element-127" data-mp="1c18tq52n66gd">
                  <Badge variant="outline" className="border-emerald-500/60 text-emerald-300 bg-emerald-500/10 px-5 py-2 text-sm md:text-base font-bold uppercase tracking-wider shadow-[0_0_20px_rgba(52,211,153,0.15)]" data-id="element-128" data-mp="xbkowtv94pnw">
                    Ahorrás $109.500
                  </Badge>
                  <p className="text-base md:text-lg font-semibold text-foreground/95" data-id="element-129" data-mp="1uvnr5q9b1r23">
                    Pago único · Acceso de por vida
                  </p>
                  <div className="inline-flex items-center gap-2 text-sm text-primary/90 font-medium" data-id="element-130" data-mp="1kyj6juv3y3ne">
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" data-id="element-131" data-mp="5hio4hcatp6h"></span>
                    Acceso inmediato tras la compra
                  </div>
                </div>
              </div>

              {/* PRIMARY CTA */}
              <div className="text-center mb-10" data-id="element-132" data-mp="l0la6gzdq9km">
                <Button asChild size="lg" className="relative w-full text-lg md:text-2xl py-9 md:py-10 h-auto font-extrabold tracking-wide bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_0_60px_rgba(212,168,87,0.6)] ring-2 ring-primary/40 hover:ring-primary/70 transition-all hover:scale-[1.02]" data-id="element-133" data-mp="kzs9nxr0kgax">
                  <a href="https://checkout.librofy.online/cart/53275043332459:1" target="_blank" rel="noopener noreferrer" data-id="element-134" data-mp="13s4bmjdr9fkj">
                    Desbloquear COSMOS
                    <ArrowRight className="ml-2 h-5 w-5" data-id="element-135" data-mp="3jih4sezbq0c8" />
                  </a>
                </Button>
              </div>

              {/* Trust badges — below CTA */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5 md:gap-3 mb-10" data-id="element-136" data-mp="3fj8b1a4aa2go">
                {[{
                icon: '🔒',
                label: 'Pago seguro'
              }, {
                icon: '⚡',
                label: 'Acceso en 5 min'
              }, {
                icon: '🛡️',
                label: 'Garantía 7 días'
              }, {
                icon: '💳',
                label: 'Mercado Pago'
              }].map((badge, i) => <div key={i} className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl bg-background/40 border border-border/40 backdrop-blur-sm text-xs md:text-sm font-medium text-foreground/85" data-id="element-137" data-mp="2iqiau0exz9o9" data-mpi={i}>
                    <span className="text-base shrink-0" aria-hidden="true" data-id="element-138" data-mp="se2miu8geov7">
                      {badge.icon}
                    </span>
                    <span data-id="element-139" data-mp="20lyrjau0qvom">{badge.label}</span>
                  </div>)}
              </div>

              {/* Countdown timer — right below trust badges */}
              <div className="mb-12" data-id="element-140" data-mp="1vp8gx30zgsal">
                <Countdown data-id="element-141" data-mp="29ng80ubc8zdu" />
              </div>

              <div className="text-center mb-12 space-y-1.5" data-id="element-142" data-mp="29o84068ukz4n">
                <p className="text-sm md:text-base text-foreground/90 font-medium" data-id="element-143" data-mp="24mshe9r0ukk5">
                  Hoy desbloqueás toda la colección COSMOS con un único acceso.
                </p>
                <p className="text-sm text-primary/90 font-semibold" data-id="element-144" data-mp="2emjdgfv8d7mc">
                  Quedan pocos accesos disponibles.
                </p>
              </div>

              <div className="flex items-center gap-4 mb-10" data-id="element-145" data-mp="2sfitqk2u75uy">
                <div className="flex-1 h-px bg-border/40" data-id="element-146" data-mp="2od48jtbp3u0i"></div>
                <span className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground/60 font-medium" data-id="element-147" data-mp="32bc9ecburfgy">
                  Desglose
                </span>
                <div className="flex-1 h-px bg-border/40" data-id="element-148" data-mp="2xaejfdklph4w"></div>
              </div>

              <div className="space-y-3 mb-10 text-sm" data-id="element-149" data-mp="3gyu72qqzz9tv">
                {[{
                name: 'COSMOS I',
                price: '$19.900',
                isMain: true
              }, {
                name: 'COSMOS II',
                price: '$19.900'
              }, {
                name: 'COSMOS III',
                price: '$19.900'
              }, {
                name: 'COSMOS IV',
                price: '$19.900'
              }, {
                name: 'COSMOS V',
                price: '$19.900'
              }, {
                name: 'Bonus Exclusivos',
                price: '$29.900'
              }].map((item, i) => <div key={i} className="flex justify-between items-center py-1.5" data-id="element-150" data-mp="2jb2c83fewkpg" data-mpi={i}>
                    <span className={item.isMain ? 'text-foreground/85 font-semibold' : 'text-muted-foreground/60'} data-id="element-151" data-mp="2zpa319hevugx">
                      {item.name}
                      {item.isMain && <span className="ml-2 text-[10px] text-primary/70 font-medium uppercase tracking-wider" data-id="element-152" data-mp="2gqxikrl1uy61">
                          (principal)
                        </span>}
                    </span>
                    <div className="flex items-center gap-2.5" data-id="element-153" data-mp="272fk4h08t35k">
                      {item.isMain ? <span className="text-primary/90 font-semibold text-sm" data-id="element-154" data-mp="223m68nh1na4v">
                          {item.price}
                        </span> : <>
                          <span className="line-through text-muted-foreground/40 text-xs" data-id="element-155" data-mp="1t24ufhao5ulz">
                            {item.price}
                          </span>
                          <span className="text-primary/70 font-medium text-[10px] uppercase tracking-wider" data-id="element-156" data-mp="ckmiydiee1eg">
                            Bonificado
                          </span>
                        </>}
                    </div>
                  </div>)}

                <div className="flex justify-between items-center pt-4 mt-4 border-t border-border/40 text-sm" data-id="element-157" data-mp="3l1hc319e87fc">
                  <span className="text-muted-foreground/70" data-id="element-158" data-mp="3q13rnhcp1zrb">
                    Valor real total:
                  </span>
                  <span className="line-through text-muted-foreground/50" data-id="element-159" data-mp="3uxkhcfzjkm6i">
                    $129.400
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* 10. Preguntas frecuentes */}
      <section id="faq" className="py-24 relative overflow-hidden" data-id="element-160" data-mp="378j2ob048ess">
        <div className="absolute inset-0 pointer-events-none" data-id="element-161" data-mp="2gzwyljlnsh30">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/[0.04] rounded-full blur-3xl" data-id="element-162" data-mp="2gxki7avjues2"></div>
        </div>

        <div className="container mx-auto px-4 max-w-3xl relative z-10" data-id="element-163" data-mp="27xxictigxh14">
          <div className="text-center mb-12" data-id="element-164" data-mp="2c013yayuq204">
            <p className="text-xs uppercase tracking-[0.35em] text-primary/70 mb-4 font-medium" data-id="element-165" data-mp="2byr3jf4j0hqb">
              Lo que querés saber
            </p>
            <h2 className="text-3xl md:text-5xl font-heading font-bold leading-tight text-foreground" data-id="element-166" data-mp="22zm8wjw358jf">
              Preguntas{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-amber-200 italic" data-id="element-167" data-mp="1ocatu80p36ek">
                frecuentes
              </span>
            </h2>
          </div>

          <Accordion type="single" collapsible className="w-full space-y-4" data-id="element-168" data-mp="1fbcptpq9ayvz">
            {[{
            q: '¿En qué formato recibo COSMOS?',
            a: 'Recibirás toda la colección en formato digital con acceso inmediato después de la compra. Podrás verla cuando quieras y avanzar a tu ritmo.'
          }, {
            q: '¿Puedo usarlo desde el celular?',
            a: 'Sí. Toda la colección está optimizada para celular, tablet y computadora para que puedas estudiar desde cualquier lugar.'
          }, {
            q: '¿Sirve si todavía me siento perdida interpretando?',
            a: 'Sí. COSMOS fue creado justamente para ayudarte a dejar de sentir caos frente a una carta natal y empezar a interpretar con estructura y claridad.'
          }, {
            q: '¿Los bonus están incluidos?',
            a: 'Sí. Al acceder hoy desbloqueás toda la colección completa junto con todos los bonus incluidos.'
          }, {
            q: '¿Cuándo obtengo acceso?',
            a: 'El acceso es inmediato. Apenas completes tu compra recibirás toda la colección automáticamente.'
          }, {
            q: '¿Tiene garantía?',
            a: 'Sí. Probá COSMOS durante 7 días. Si no cumple tus expectativas, te devolvemos el 100% de tu dinero.'
          }].map((faq, i) => <AccordionItem key={i} value={`item-${i}`} className="bg-card/60 backdrop-blur-sm border border-border/50 rounded-2xl px-6 md:px-7 data-[state=open]:border-primary/40 data-[state=open]:bg-card/80 transition-all" data-id="element-169" data-mp="18rlwj44kj4o8" data-mpi={i}>
                <AccordionTrigger className="text-left font-heading font-semibold text-base md:text-lg hover:text-primary py-6 leading-snug" data-id="element-170" data-mp="1npe7diz674w3">
                  {faq.q}
                </AccordionTrigger>
                <AccordionContent className="text-foreground/80 text-base pb-6 leading-relaxed" data-id="element-171" data-mp="3pgrca8151cs5">
                  {faq.a}
                </AccordionContent>
              </AccordionItem>)}
          </Accordion>

          <div className="relative pt-12 mt-12 text-center" data-id="element-172" data-mp="3lytx6xmganhm">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-px bg-primary/40" data-id="element-173" data-mp="3h7tybmtrtvch"></div>
            <p className="font-heading text-xl md:text-2xl italic text-primary leading-relaxed max-w-2xl mx-auto" data-id="element-174" data-mp="3c8qs3z221j2x">
              "No necesitás memorizar{' '}
              <span className="font-semibold" data-id="element-175" data-mp="388qdadb1n5vc">más información</span>.
              <br className="hidden md:block" data-id="element-176" data-mp="8sa7svkhir14" />
              Necesitás un{' '}
              <span className="font-semibold" data-id="element-177" data-mp="3t72da0leism">sistema para entenderla</span>."
            </p>
          </div>
        </div>
      </section>

      {/* 11. Final CTA section */}
      <section className="py-32 relative overflow-hidden bg-card border-t border-border/50 text-center" data-id="element-178" data-mp="3vyug4iger1la">
        <div className="absolute inset-0 pointer-events-none" data-id="element-179" data-mp="3qzzmsmktdumx">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-primary/10 rounded-full blur-3xl" data-id="element-180" data-mp="2kicmsr83xf2v"></div>
        </div>
        <div className="container mx-auto px-4 max-w-3xl relative z-10" data-id="element-181" data-mp="799zq72lcfwy">
          <h2 className="text-4xl md:text-5xl font-heading font-bold mb-6 text-foreground" data-id="element-182" data-mp="77yue2thz03q">
            Tu próxima interpretación comienza hoy.
          </h2>
          <p className="text-xl text-muted-foreground mb-12" data-id="element-183" data-mp="3ufsr1n6h14hc">
            La diferencia entre sentirte perdida y sentir claridad comienza con
            un sistema.
          </p>
          <Button asChild size="lg" className="text-xl px-10 py-8 h-auto bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_0_30px_rgba(212,168,87,0.4)] transition-all hover:scale-105 w-full sm:w-auto" data-id="element-184" data-mp="q01ddktm2sh5">
            <a href="https://checkout.librofy.online/cart/53275043332459:1" target="_blank" rel="noopener noreferrer" data-id="element-185" data-mp="lyo7xbufkrar">
              DESBLOQUEAR COSMOS
            </a>
          </Button>
        </div>
      </section>

      {/* 12. Footer — Recursos Fitness Pro */}
      <footer className="py-10 bg-[#0a0e1a] border-t border-border/40 text-muted-foreground" data-id="element-186" data-mp="2y7db7iej8pwk">
        <div className="container mx-auto px-4" data-id="element-187" data-mp="2u6kyugad64w2">
          <div className="flex flex-col md:flex-row items-center md:items-center justify-between gap-6 text-center md:text-left" data-id="element-188" data-mp="2u61j9686rsl7">
            <div className="flex items-center gap-3" data-id="element-189" data-mp="336zotsy9w85w">
              <img src="https://cdn.magicpatterns.com/uploads/vFoBSfKKUQVrfA3NJeBjJC/logo_sf.png" alt="Recursos Fitness Pro" className="h-12 w-12 rounded-full object-cover shrink-0" loading="lazy" decoding="async" data-id="element-190" data-mp="338apvl0c31tb" />
              <div data-id="element-191" data-mp="2p8t1v7uv3sl0">
                <p className="font-heading font-bold tracking-wide text-foreground text-sm md:text-base" data-id="element-192" data-mp="2p91d6vbc6pu3">
                  RECURSOS FITNESS PRO
                </p>
                <p className="text-xs md:text-sm text-muted-foreground" data-id="element-193" data-mp="1wpma57vo1bl7">
                  © Recursos Fitness Pro 2026 — Todos los derechos reservados.
                </p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-6 text-sm" data-id="element-194" data-mp="3rtkb2n04r7mz">
              <a href="mailto:soporterecursosfitnesspro@gmail.com" className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors" data-id="element-195" data-mp="m7tw7z8yu72">
                <Mail className="h-4 w-4" data-id="element-196" data-mp="9i725vqg1flx" />
                <span data-id="element-197" data-mp="38vr85kpiaxpl">soporterecursosfitnesspro@gmail.com</span>
              </a>
              <a href="https://www.instagram.com/recursos.fitness.pro" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors" data-id="element-198" data-mp="3duayryf1oxoa">
                <Instagram className="h-4 w-4" data-id="element-199" data-mp="x5ox20508tt4" />
                <span data-id="element-200" data-mp="x4vpiwy8209x">Instagram: @recursos.fitness.pro</span>
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>;
}